import type * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Link2,
  Copy,
  Check,
  Eye,
  EyeOff,
  RefreshCw,
  LayoutGrid,
  Crown,
  Monitor,
  Smartphone,
  X,
  ArrowLeft,
  Share2,
  Lock,
  Maximize2,
  ExternalLink,
  MoreHorizontal,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { PageHero } from "@/components/PageHero";
import { Switch } from "@/components/ui/switch";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useIsPremium } from "@/hooks/useIsPremium";
import { getMyMiniSite, updateMiniSite } from "@/lib/mini-site.functions";
import { getTemplate, MINI_SITE_TEMPLATES, type MiniSiteTemplateId } from "@/lib/mini-site-templates";
import { TemplateThumb } from "@/components/mini-site/TemplateThumb";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PageSkeleton } from "@/components/ui/loading-state";

export const Route = createFileRoute("/mini-site/")({
  head: () => ({
    meta: [
      { title: "Mini Site da Coleção — Viny Club" },
      {
        name: "description",
        content:
          "Monte o mini site público da sua coleção de vinil: escolha um template, controle a visibilidade e compartilhe seu link.",
      },
      { property: "og:title", content: "Mini Site da Coleção — Viny Club" },
      {
        property: "og:description",
        content: "Escolha um template, publique e compartilhe a sua coleção de vinil em um link só.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: MiniSitePage,
});

function MiniSitePage() {
  const { profile } = useCurrentUser();
  const { isPremium } = useIsPremium();
  const isMobile = useIsMobile();
  const [copied, setCopied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [device, setDevice] = useState<"desktop" | "phone">("phone");
  const [reloadKey, setReloadKey] = useState(0);

  const fetchSite = useServerFn(getMyMiniSite);
  const update = useServerFn(updateMiniSite);
  const qc = useQueryClient();

  const siteQ = useQuery({ queryKey: ["my-mini-site"], queryFn: () => fetchSite() });
  const site = siteQ.data;
  const tpl = site ? getTemplate(site.template_id) : null;

  const setField = useMutation({
    mutationFn: (patch: Parameters<typeof update>[0]["data"]) => update({ data: patch }),
    onSuccess: (data) => {
      qc.setQueryData(["my-mini-site"], data);
      setReloadKey((k) => k + 1);
    },
    onError: (e: Error) => {
      if (e.message?.includes("PREMIUM_REQUIRED")) toast.error("Template exclusivo para Embaixadores");
      else toast.error("Não consegui salvar");
    },
  });

  const username = profile?.username ?? "";
  const previewPath = useMemo(() => (username ? `/${username}` : "/"), [username]);
  const prettyUrl = username ? `viny.club/${username}` : "viny.club/";
  const fullUrl =
    (typeof window !== "undefined" ? window.location.origin : "https://viny.club") + previewPath;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`https://viny.club/${username}`);
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não consegui copiar");
    }
  };

  const share = async () => {
    if (typeof navigator !== "undefined" && (navigator as any).share) {
      try {
        await (navigator as any).share({
          title: "Meu mini site",
          url: `https://viny.club/${username}`,
        });
      } catch {
        /* cancelado */
      }
    } else {
      void copy();
    }
  };

  if (!site) {
    return (
      <AppShell>
        <PageSkeleton rows={4} message="Carregando seu mini site…" />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl">
        <PageHero
          eyebrow="Mini site"
          title="Sua coleção em um link"
          subtitle="Monte a página pública da sua coleção e compartilhe onde quiser."
          actions={
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold ${
                site.is_public
                  ? "bg-emerald-400/15 text-emerald-400"
                  : "bg-white/10 text-white/70"
              }`}
            >
              {site.is_public ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {site.is_public ? "Publicado" : "Rascunho"}
            </span>
          }
        />
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-6 lg:px-8 lg:pb-16">


        {/* ── Link + ações ───────────────────────────── */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={copy}
            className="ios-press flex min-w-0 flex-1 items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-surface/50 px-4 py-2.5 text-left"
          >
            <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate font-mono text-sm">{prettyUrl}</span>
            <span className="shrink-0 text-muted-foreground">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </span>
          </button>

          {/* Desktop: ações lado a lado */}
          <div className="hidden items-center gap-2 sm:flex">
            <button
              onClick={share}
              className="ios-press inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--hairline)] bg-surface/50 px-4 py-2.5 text-sm font-semibold hover:bg-surface"
            >
              <Share2 className="h-4 w-4" /> Compartilhar
            </button>
            <a
              href={fullUrl}
              target="_blank"
              rel="noreferrer"
              className="ios-press inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background"
            >
              <ExternalLink className="h-4 w-4" /> Abrir
            </a>
          </div>

          {/* Mobile: 1 ação principal + demais no bottom sheet */}
          <div className="flex items-center gap-2 sm:hidden">
            <a
              href={fullUrl}
              target="_blank"
              rel="noreferrer"
              className="ios-press inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-foreground px-4 py-3 text-sm font-semibold text-background"
            >
              <ExternalLink className="h-4 w-4" /> Ver meu site
            </a>
            <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Mais ações"
                  className="ios-press grid h-11 w-11 shrink-0 place-items-center rounded-full border border-foreground/10 bg-foreground/[0.05]"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-[28px] border-t border-white/15 bg-background/85 p-0 text-foreground backdrop-blur-2xl"
              >
                <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-foreground/20" />
                <div className="space-y-1.5 p-4 pb-8">
                  <div className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Ações do mini site
                  </div>
                  <SheetAction
                    icon={<Copy className="h-4.5 w-4.5" />}
                    label={copied ? "Link copiado" : "Copiar link"}
                    onClick={() => {
                      void copy();
                    }}
                  />
                  <SheetAction
                    icon={<Share2 className="h-4.5 w-4.5" />}
                    label="Compartilhar"
                    onClick={() => {
                      setActionsOpen(false);
                      void share();
                    }}
                  />
                  <SheetAction
                    icon={
                      site.is_public ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />
                    }
                    label={site.is_public ? "Deixar como rascunho" : "Publicar mini site"}
                    onClick={() => {
                      setField.mutate({ is_public: !site.is_public });
                      setActionsOpen(false);
                    }}
                  />
                  <SheetAction
                    icon={<Maximize2 className="h-4.5 w-4.5" />}
                    label="Pré-visualizar"
                    onClick={() => {
                      setActionsOpen(false);
                      setPreviewOpen(true);
                    }}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>


        {/* ── Workspace ──────────────────────────────── */}
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          {/* Live preview (desktop only) */}
          <section className="hidden overflow-hidden rounded-3xl border border-[color:var(--hairline)] bg-surface/30 lg:block">
            <div className="flex items-center justify-between gap-2 border-b border-[color:var(--hairline)] px-3 py-2.5">
              <div className="flex rounded-full border border-[color:var(--hairline)] p-0.5 text-[11px]">
                <button
                  onClick={() => setDevice("phone")}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${
                    device === "phone" ? "bg-foreground text-background" : "text-muted-foreground"
                  }`}
                >
                  <Smartphone className="h-3.5 w-3.5" /> Mobile
                </button>
                <button
                  onClick={() => setDevice("desktop")}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${
                    device === "desktop" ? "bg-foreground text-background" : "text-muted-foreground"
                  }`}
                >
                  <Monitor className="h-3.5 w-3.5" /> Desktop
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setReloadKey((k) => k + 1)}
                  className="grid h-8 w-8 place-items-center rounded-full bg-surface text-muted-foreground hover:text-foreground"
                  aria-label="Atualizar pré-visualização"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setPreviewOpen(true)}
                  className="grid h-8 w-8 place-items-center rounded-full bg-surface text-muted-foreground hover:text-foreground"
                  aria-label="Tela cheia"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="grid place-items-center bg-[color:var(--background)] p-4 sm:p-6">
              {device === "phone" ? (
                <div className="relative h-[560px] w-[300px] overflow-hidden rounded-[38px] border border-[color:var(--hairline)] bg-black p-2.5 shadow-[var(--shadow-soft)]">
                  <div className="pointer-events-none absolute left-1/2 top-[13px] z-10 h-4 w-20 -translate-x-1/2 rounded-full bg-black" />
                  <div className="h-full w-full overflow-hidden rounded-[28px] bg-white">
                    {username && (
                      <iframe
                        key={`p-${reloadKey}`}
                        src={previewPath}
                        title="Pré-visualização mobile"
                        className="h-full w-full border-0"
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
              ) : (
                <ScaledFrame src={previewPath} keyId={`d-${reloadKey}`} enabled={!!username} />
              )}
            </div>
          </section>

          {/* Controls */}
          <div className="flex flex-col gap-4">
            {/* Visibility */}
            <section className="order-2 rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-4 lg:order-none">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground lg:hidden">
                Etapa 2 · Quem pode ver
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Visibilidade</div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {site.is_public
                      ? "Qualquer pessoa com o link consegue ver."
                      : "Somente você consegue acessar."}
                  </div>
                </div>
                <Switch
                  checked={site.is_public}
                  disabled={setField.isPending}
                  onCheckedChange={(v) => setField.mutate({ is_public: v })}
                />
              </div>
            </section>

            {/* Template picker */}
            <section className="order-1 rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-4 lg:order-none">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground lg:hidden">
                Etapa 1 · Escolha o template
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">Template</div>
                <Link
                  to="/mini-site/templates"
                  className="inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1 text-[11px] font-semibold hover:bg-foreground hover:text-background"
                >
                  <LayoutGrid className="h-3 w-3" /> Ver todos
                </Link>
              </div>
              <div className="mt-1 text-[12px] text-muted-foreground">
                Em uso: <span className="text-foreground">{tpl?.name}</span>
              </div>

              {/* Mobile: lista em linhas, estilo app */}
              <div className="mt-3 space-y-2 lg:hidden">
                {MINI_SITE_TEMPLATES.map((t) => {
                  const locked = t.tier === "pro" && !isPremium;
                  const current = site.template_id === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      disabled={setField.isPending}
                      onClick={() => {
                        if (locked) {
                          toast.error("Template exclusivo para Embaixadores");
                          return;
                        }
                        if (current) return;
                        setField.mutate({ template_id: t.id as MiniSiteTemplateId });
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl border p-2 text-left transition active:scale-[0.99] ${
                        current
                          ? "border-foreground bg-surface/60"
                          : "border-[color:var(--hairline)] bg-surface/30"
                      }`}
                    >
                      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-neutral-900">
                        <div className="absolute inset-0">
                          <TemplateThumb tpl={t} />
                        </div>
                        {locked && (
                          <div className="absolute inset-0 grid place-items-center bg-black/45">
                            <Lock className="h-3.5 w-3.5 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-[13px] font-semibold">{t.name}</span>
                          {t.isNew && !current && (
                            <span className="shrink-0 rounded-full bg-emerald-400 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-black">
                              novo
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {t.tier === "pro" ? "Pro · Embaixadores" : "Grátis"}
                        </div>
                      </div>
                      {current ? (
                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground text-background">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      ) : locked ? (
                        <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <span className="shrink-0 rounded-full border border-[color:var(--hairline)] px-2.5 py-1 text-[10px] font-semibold text-muted-foreground">
                          Usar
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Desktop: grid de cards */}
              <div className="hidden lg:grid lg:grid-cols-2 lg:gap-2">
                {MINI_SITE_TEMPLATES.map((t) => {
                  const locked = t.tier === "pro" && !isPremium;
                  const current = site.template_id === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        if (locked) {
                          toast.error("Template exclusivo para Embaixadores");
                          return;
                        }
                        if (current) return;
                        setField.mutate({ template_id: t.id as MiniSiteTemplateId });
                      }}
                      className={`group relative w-[132px] shrink-0 snap-start overflow-hidden rounded-xl border text-left transition lg:w-auto ${
                        current
                          ? "border-foreground ring-1 ring-foreground"
                          : "border-[color:var(--hairline)] hover:border-foreground/40"
                      }`}
                    >
                      <div className="relative aspect-[4/3] w-full bg-neutral-900">
                        <div className="absolute inset-0">
                          <TemplateThumb tpl={t} />
                        </div>
                        {locked && (
                          <div className="absolute inset-0 grid place-items-center bg-black/45">
                            <Lock className="h-4 w-4 text-white" />
                          </div>
                        )}
                        {current && (
                          <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-foreground text-background">
                            <Check className="h-3 w-3" />
                          </span>
                        )}
                        {t.isNew && !current && (
                          <span className="absolute left-1.5 top-1.5 rounded-full bg-emerald-400 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-black">
                            novo
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                        <span className="truncate text-[12px] font-semibold">{t.name}</span>
                        <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                          {t.tier === "pro" ? "Pro" : "Free"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {!isPremium && (
              <section className="order-3 rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-foreground/10">
                    <Crown className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">Templates Pro</div>
                    <div className="text-[12px] text-muted-foreground">
                      Layouts exclusivos para Embaixadores.
                    </div>
                  </div>
                </div>
                <Link
                  to="/assinar"
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background"
                >
                  <Crown className="h-3.5 w-3.5" /> Apoie o projeto
                </Link>
              </section>
            )}
          </div>
        </div>
      </div>

      {previewOpen && (
        <PreviewDrawer
          src={previewPath}
          onClose={() => setPreviewOpen(false)}
          username={username}
          initialDevice={isMobile ? "phone" : "desktop"}
        />
      )}
    </AppShell>
  );
}

/* ────────── Desktop scaled iframe ────────── */
function ScaledFrame({ src, keyId, enabled }: { src: string; keyId: string; enabled: boolean }) {
  return (
    <div className="w-full overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-white">
      <div className="flex h-7 items-center gap-1.5 border-b border-black/10 bg-neutral-100 px-3">
        <span className="h-2 w-2 rounded-full bg-neutral-300" />
        <span className="h-2 w-2 rounded-full bg-neutral-300" />
        <span className="h-2 w-2 rounded-full bg-neutral-300" />
      </div>
      <div className="relative aspect-[16/10] w-full">
        {enabled && (
          <iframe
            key={keyId}
            src={src}
            title="Pré-visualização desktop"
            loading="lazy"
            className="absolute left-0 top-0 border-0 bg-white"
            style={{
              width: "1280px",
              height: "800px",
              transform: "scale(var(--preview-scale, 0.5))",
              transformOrigin: "top left",
            }}
            ref={(el) => {
              if (!el) return;
              const parent = el.parentElement;
              if (!parent) return;
              const apply = () => {
                const scale = parent.clientWidth / 1280;
                el.style.setProperty("--preview-scale", String(scale));
                el.style.height = `${parent.clientHeight / scale}px`;
              };
              apply();
              const ro = new ResizeObserver(apply);
              ro.observe(parent);
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ────────── Preview Drawer (full screen) ────────── */
function PreviewDrawer({
  src,
  onClose,
  username,
  initialDevice = "desktop",
}: {
  src: string;
  onClose: () => void;
  username: string;
  initialDevice?: "desktop" | "phone";
}) {
  const [device, setDevice] = useState<"desktop" | "phone">(initialDevice);
  const [key, setKey] = useState(0);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/90 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-black/80 px-4 py-3 text-white sm:px-6">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-white/80 hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Voltar para edição</span>
        </button>

        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-white/15 p-0.5 text-[11px]">
            <button
              onClick={() => setDevice("desktop")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${
                device === "desktop" ? "bg-white text-black" : "text-white/70"
              }`}
            >
              <Monitor className="h-3.5 w-3.5" /> Desktop
            </button>
            <button
              onClick={() => setDevice("phone")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${
                device === "phone" ? "bg-white text-black" : "text-white/70"
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" /> Mobile
            </button>
          </div>
          <button
            onClick={() => setKey((k) => k + 1)}
            className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Atualizar"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>

        <button
          onClick={onClose}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <div className="grid h-full place-items-center p-4 sm:p-8">
          {device === "desktop" ? (
            <div className="relative h-full w-full max-w-[1280px] overflow-hidden rounded-xl border border-white/10 bg-neutral-900 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]">
              <div className="flex h-8 items-center gap-1.5 border-b border-white/10 px-3">
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-700" />
                <span className="ml-3 truncate text-[11px] text-white/40">viny.club/{username}</span>
              </div>
              {username && (
                <iframe
                  key={`d-${key}`}
                  src={src}
                  title="Pré-visualização desktop"
                  className="h-[calc(100%-2rem)] w-full border-0 bg-background"
                />
              )}
            </div>
          ) : (
            <div className="relative h-full max-h-[820px] w-full max-w-[380px] overflow-hidden rounded-[42px] border border-white/15 bg-neutral-950 p-3 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]">
              <div className="pointer-events-none absolute left-1/2 top-[14px] z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-neutral-950" />
              <div className="relative h-full w-full overflow-hidden rounded-[30px] bg-background">
                {username && (
                  <iframe
                    key={`p-${key}`}
                    src={src}
                    title="Pré-visualização mobile"
                    className="h-full w-full border-0"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────── Linha de ação do bottom sheet (estilo iOS) ────────── */
function SheetAction({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ios-press flex w-full items-center gap-3 rounded-2xl border border-foreground/10 bg-foreground/[0.04] px-4 py-3.5 text-left text-[15px] font-medium"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-foreground/[0.07] text-foreground">
        {icon}
      </span>
      {label}
    </button>
  );
}
